import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Paper
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';
import axios from 'axios';

const RegionalKeywordsRankingAnalysisTable = ({ locationId, dateRange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (dateRange?.startDate && dateRange?.endDate && locationId) {
      fetchRegionalAnalysis();
    }
  }, [locationId, dateRange]);

  const fetchRegionalAnalysis = async () => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('📊 Fetching regional analysis for:', {
        locationId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });

      const response = await axios.post(
        `/api/businesses/${locationId}/regional-keywords-analysis`,
        {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        },
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      console.log('📊 Response received:', response.data);

      if (response.data.success) {
        setData(response.data.data);
        setError(null);
      } else {
        const errorMsg = response.data.error || response.data.data?.message || 'Failed to fetch regional analysis data';
        console.error('❌ API returned error:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('❌ Error fetching regional analysis:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to fetch regional analysis data';
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!dateRange?.startDate || !dateRange?.endDate) {
    return null;
  }

  return (
    <Card sx={{ mb: 4 }}>
      <CardHeader title="Regional Keywords Ranking Analysis" />
      <CardContent>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography color="textSecondary">Loading regional analysis...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="warning">{error}</Alert>
        ) : data?.keywords && data.keywords.length > 0 ? (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Comparing {data.keywords.length} keywords across 19 regional test points
              {data.baselineDate && data.comparableDate && (
                <>
                  {' '}from {new Date(data.baselineDate).toLocaleDateString()} to{' '}
                  {new Date(data.comparableDate).toLocaleDateString()}
                </>
              )}
            </Typography>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Keyword</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">
                      Positive Progress
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">
                      Negative Progress
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">
                      Net Change
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.keywords.map((keyword, index) => {
                    const netChange = keyword.improvements - keyword.declines;
                    const netChangeColor = netChange > 0 ? '#4caf50' : netChange < 0 ? '#f44336' : '#9e9e9e';

                    return (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {keyword.keyword}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                color: '#4caf50'
                              }}
                            >
                              {keyword.improvements}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <TrendingDown sx={{ color: '#f44336', fontSize: 20 }} />
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                                color: '#f44336'
                              }}
                            >
                              {keyword.declines}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">


                         

                          <Typography
                              sx={{
                                fontWeight: 'bold',
                                color: netChangeColor
                              }}
                            >
                              {`${netChange > 0 ? '+' : ''}${netChange}`}
                            </Typography>


                       



                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {data.message && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                {data.message}
              </Typography>
            )}
          </>
        ) : (
          <Alert severity="info">
            No ranking data available for the selected date range. Please ensure snapshots exist for the specified dates.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default RegionalKeywordsRankingAnalysisTable;
